import jsPDF from "jspdf";
import { SessionReport, SessionReportItem } from "@/types/sessionReport";
import { BRAND } from "@/config/brand";
import logoImage from "@/assets/regard-de-nature-logo.png";

// Couleurs de la marque REGARD DE NATURE
const colors = {
  primary: BRAND.colors.rgb.forestGreen,       // Vert forêt
  secondary: BRAND.colors.rgb.softAccent,      // Accent doux
  accent: BRAND.colors.rgb.gold,               // Doré
  background: BRAND.colors.rgb.lightBackground, // Fond clair
  text: BRAND.colors.rgb.text,
  textLight: BRAND.colors.rgb.textLight,
  white: BRAND.colors.rgb.white,
};

interface ChildInfo {
  name: string;
  age?: string;
  gender?: string | null;
}

export const generateSessionReportPDF = (
  report: SessionReport,
  patientName: string,
  signatureDataUrl?: string,
  childInfo?: ChildInfo
): Uint8Array => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  const lineHeight = 6.5;
  let yPos = margin;

  const checkPageBreak = (extraSpace: number = 15) => {
    if (yPos + extraSpace > pageHeight - margin - 15) {
      doc.addPage();
      yPos = margin;

      // Watermark discret sur les nouvelles pages
      doc.setFontSize(8);
      doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
      doc.setFont("helvetica", "italic");
      doc.text("Compte Rendu de Seance", pageWidth / 2, margin - 5, { align: "center" });
    }
  };

  const addSection = (title: string) => {
    checkPageBreak(20);

    // Bandeau de couleur pour le titre de section
    doc.setFillColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
    doc.roundedRect(margin - 2, yPos - 5, pageWidth - margin * 2 + 4, 10, 2, 2, "F");

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text(title, margin + 2, yPos);
    yPos += lineHeight * 2.2;
  };

  const addItems = (items: SessionReportItem[]) => {
    // Filtrer les items vides
    const validItems = items.filter((item) => item.content && item.content.trim());
    
    validItems.forEach((item) => {
      const maxWidth = pageWidth - margin * 2 - 15;
      const lines = doc.splitTextToSize(item.content, maxWidth);

      checkPageBreak(lines.length * lineHeight + 4);

      // Tiret
      doc.setFontSize(9.5);
      doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
      doc.text("-", margin + 4, yPos);

      // Contenu
      doc.setFont("helvetica", "normal");
      doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
      doc.text(lines, margin + 10, yPos);
      yPos += lines.length * lineHeight + 2;
    });
  };

  // Labels pour les types de recommandations
  const getRecommendationTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      phytotherapie: "Phytotherapie",
      complement: "Complement alimentaire",
      aromatherapie: "Aromatherapie",
      gemmotherapie: "Gemmotherapie",
      fleurs_bach: "Fleurs de Bach",
      alimentation: "Alimentation",
      hygiene_vie: "Hygiene de vie",
      autre: "Autre",
    };
    return labels[type] || type;
  };

  // Ajout des recommandations groupées par type
  const addItemsGroupedByType = (items: SessionReportItem[]) => {
    const grouped = items.reduce((acc, item) => {
      const type = item.originalType || "autre";
      if (!acc[type]) acc[type] = [];
      acc[type].push(item);
      return acc;
    }, {} as Record<string, SessionReportItem[]>);

    const typeOrder = [
      "phytotherapie",
      "aromatherapie",
      "gemmotherapie",
      "fleurs_bach",
      "complement",
      "alimentation",
      "hygiene_vie",
      "autre",
    ];

    typeOrder.forEach((type) => {
      const typeItems = grouped[type];
      if (!typeItems || typeItems.length === 0) return;

      checkPageBreak(lineHeight * 2);

      // Sous-titre de catégorie
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(colors.accent.r, colors.accent.g, colors.accent.b);
      doc.text(getRecommendationTypeLabel(type).toUpperCase(), margin + 2, yPos);
      yPos += lineHeight;

      // Ligne de séparation
      doc.setDrawColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
      doc.setLineWidth(0.3);
      doc.line(margin + 2, yPos, pageWidth - margin - 2, yPos);
      yPos += 4;

      typeItems.forEach((item) => {
        const raw = item.content?.trim();
        if (!raw) return;

        const parts = raw
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter(Boolean);

        const title = parts[0] || "";
        const body = parts.slice(1).join("\n");

        const titleMaxWidth = pageWidth - margin * 2 - 8;
        const titleLines = doc.splitTextToSize(title, titleMaxWidth);

        const bodyMaxWidth = pageWidth - margin * 2 - 12;
        const bodyLines = body ? doc.splitTextToSize(body, bodyMaxWidth) : [];

        checkPageBreak((titleLines.length + bodyLines.length) * lineHeight + 12);

        // Titre de la recommandation
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
        doc.text(titleLines, margin + 4, yPos);
        yPos += titleLines.length * lineHeight;

        // Corps de la recommandation
        if (bodyLines.length > 0) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9.5);
          doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
          doc.text(bodyLines, margin + 6, yPos);
          yPos += bodyLines.length * lineHeight;
        }

        yPos += 5;
      });

      yPos += 2;
    });
  };

  // En-tête avec branding REGARD DE NATURE
  doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
  doc.rect(0, 0, pageWidth, 65, "F");

  // Barre d'accent
  doc.setFillColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
  doc.rect(0, 65, pageWidth, 3, "F");

  // Logo
  try {
    doc.addImage(logoImage, "PNG", pageWidth / 2 - 18, 6, 36, 36);
  } catch (error) {
    console.log("Logo non charge");
  }

  // Nom de marque
  doc.setTextColor(colors.accent.r, colors.accent.g, colors.accent.b);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(BRAND.name.line1, pageWidth / 2, 48, { align: "center" });

  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.setFontSize(16);
  doc.text(BRAND.name.line2, pageWidth / 2, 55, { align: "center" });

  // Baseline
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text(BRAND.baseline.parts.text1, pageWidth / 2 - 15, 62, { align: "right" });
  doc.setTextColor(colors.accent.r, colors.accent.g, colors.accent.b);
  doc.text(" & ", pageWidth / 2, 62, { align: "center" });
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text(BRAND.baseline.parts.text2, pageWidth / 2 + 15, 62, { align: "left" });

  yPos = 78;

  // Titre du document
  doc.setFontSize(18);
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.setFont("helvetica", "bold");
  doc.text("COMPTE RENDU DE SEANCE", pageWidth / 2, yPos, { align: "center" });

  yPos += 12;

  // Bloc infos patient
  const infoBoxHeight = childInfo ? 30 : 22;
  doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
  doc.roundedRect(margin, yPos, pageWidth - margin * 2, infoBoxHeight, 3, 3, "F");

  doc.setFontSize(12);
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.setFont("helvetica", "bold");
  
  if (childInfo) {
    doc.text(`Enfant: ${childInfo.name}${childInfo.age ? ` (${childInfo.age})` : ''}`, margin + 5, yPos + 8);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text(`Parent/Responsable: ${patientName}`, margin + 5, yPos + 15);
    doc.text(`Accompagnement: ${report.accompaniment_title}`, margin + 5, yPos + 22);
  } else {
    doc.text(`Consultant: ${patientName}`, margin + 5, yPos + 8);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text(`Accompagnement: ${report.accompaniment_title}`, margin + 5, yPos + 15);
  }

  doc.setFontSize(9);
  doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
  const dateStr = new Date(report.report_date).toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.text(`Date: ${dateStr}`, pageWidth - margin - 5, yPos + 8, { align: "right" });

  yPos += infoBoxHeight + 10;

  // Filtrer les items avec contenu réel
  const hasContent = (items: SessionReportItem[] | undefined) => 
    items?.some((item) => item.content && item.content.trim()) || false;

  // Sections
  if (hasContent(report.hygiene_vie)) {
    addSection("1. AJUSTEMENTS D'HYGIENE DE VIE");
    addItems(report.hygiene_vie);
    yPos += 6;
  }

  if (hasContent(report.conseils_alimentaires)) {
    addSection("2. CONSEILS ALIMENTAIRES");
    addItems(report.conseils_alimentaires);
    yPos += 6;
  }

  const manualSoutiensNaturels = report.soutiens_naturels?.filter((item) => item.source !== "analysis" && item.content && item.content.trim()) || [];
  if (manualSoutiensNaturels.length > 0) {
    addSection("3. SOUTIENS NATURELS RECOMMANDES");
    addItems(manualSoutiensNaturels);
    yPos += 6;
  }

  if (hasContent(report.routine_bien_etre)) {
    addSection("4. ROUTINE BIEN-ETRE PERSONNALISEE");
    addItems(report.routine_bien_etre);
    yPos += 6;
  }

  // Section 5: Recommandations de l'analyse
  const analysisRecommendations = report.soutiens_naturels?.filter((item) => item.source === "analysis" && item.content && item.content.trim()) || [];
  if (analysisRecommendations.length > 0) {
    addSection("5. RECOMMANDATIONS DE L'ANALYSE");
    addItemsGroupedByType(analysisRecommendations);
    yPos += 6;
  }

  if (report.notes && report.notes.trim()) {
    checkPageBreak(30);
    addSection("NOTES COMPLEMENTAIRES");

    const maxWidth = pageWidth - margin * 2 - 8;
    const lines = doc.splitTextToSize(report.notes, maxWidth);

    doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
    const boxHeight = lines.length * lineHeight + 6;
    doc.roundedRect(margin + 2, yPos - 3, pageWidth - margin * 2 - 4, boxHeight, 2, 2, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text(lines, margin + 6, yPos + 2);
    yPos += lines.length * lineHeight + 10;
  }

  // Signature
  if (signatureDataUrl) {
    checkPageBreak(50);
    yPos += 10;

    doc.setFontSize(10);
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.setFont("helvetica", "bold");
    doc.text("Signature du praticien:", margin, yPos);
    yPos += 8;

    try {
      doc.addImage(signatureDataUrl, "PNG", margin, yPos, 50, 25);
      yPos += 30;
    } catch (error) {
      console.log("Signature non chargee");
    }
  }

  // Footer sur toutes les pages
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Ligne de footer
    doc.setDrawColor(colors.accent.r, colors.accent.g, colors.accent.b);
    doc.setLineWidth(1);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);

    // Numéro de page
    doc.setFontSize(8);
    doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
    doc.setFont("helvetica", "normal");
    doc.text(`Page ${i} sur ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: "center" });

    // Infos du document
    doc.setFontSize(7);
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text(BRAND.legal.confidential, margin, pageHeight - 8);
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text(`${BRAND.name.full} - ${BRAND.baseline.full}`, pageWidth - margin, pageHeight - 8, { align: "right" });
  }

  // Retourner le PDF en Uint8Array pour pièce jointe email
  return doc.output("arraybuffer") as unknown as Uint8Array;
};

export const downloadSessionReportPDF = (
  report: SessionReport,
  patientName: string,
  signatureDataUrl?: string,
  childInfo?: ChildInfo
) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20;
  const lineHeight = 6.5;
  let yPos = margin;

  const checkPageBreak = (extraSpace: number = 15) => {
    if (yPos + extraSpace > pageHeight - margin - 15) {
      doc.addPage();
      yPos = margin;
      doc.setFontSize(8);
      doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
      doc.setFont("helvetica", "italic");
      doc.text("Compte Rendu de Seance", pageWidth / 2, margin - 5, { align: "center" });
    }
  };

  const addSection = (title: string) => {
    checkPageBreak(20);
    doc.setFillColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
    doc.roundedRect(margin - 2, yPos - 5, pageWidth - margin * 2 + 4, 10, 2, 2, "F");
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text(title, margin + 2, yPos);
    yPos += lineHeight * 2.2;
  };

  const addItems = (items: SessionReportItem[]) => {
    // Filtrer les items vides
    const validItems = items.filter((item) => item.content && item.content.trim());
    
    validItems.forEach((item) => {
      const maxWidth = pageWidth - margin * 2 - 15;
      const lines = doc.splitTextToSize(item.content, maxWidth);
      checkPageBreak(lines.length * lineHeight + 4);
      doc.setFontSize(9.5);
      doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
      doc.text("-", margin + 4, yPos);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
      doc.text(lines, margin + 10, yPos);
      yPos += lines.length * lineHeight + 2;
    });
  };

  const getRecommendationTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      phytotherapie: "Phytotherapie",
      complement: "Complement alimentaire",
      aromatherapie: "Aromatherapie",
      gemmotherapie: "Gemmotherapie",
      fleurs_bach: "Fleurs de Bach",
      alimentation: "Alimentation",
      hygiene_vie: "Hygiene de vie",
      autre: "Autre",
    };
    return labels[type] || type;
  };

  const addItemsGroupedByType = (items: SessionReportItem[]) => {
    const grouped = items.reduce((acc, item) => {
      const type = item.originalType || "autre";
      if (!acc[type]) acc[type] = [];
      acc[type].push(item);
      return acc;
    }, {} as Record<string, SessionReportItem[]>);

    const typeOrder = [
      "phytotherapie",
      "aromatherapie",
      "gemmotherapie",
      "fleurs_bach",
      "complement",
      "alimentation",
      "hygiene_vie",
      "autre",
    ];

    typeOrder.forEach((type) => {
      const typeItems = grouped[type];
      if (!typeItems || typeItems.length === 0) return;

      checkPageBreak(lineHeight * 2);

      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(colors.accent.r, colors.accent.g, colors.accent.b);
      doc.text(getRecommendationTypeLabel(type).toUpperCase(), margin + 2, yPos);
      yPos += lineHeight;

      doc.setDrawColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
      doc.setLineWidth(0.3);
      doc.line(margin + 2, yPos, pageWidth - margin - 2, yPos);
      yPos += 4;

      typeItems.forEach((item) => {
        const raw = item.content?.trim();
        if (!raw) return;

        const parts = raw
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter(Boolean);

        const title = parts[0] || "";
        const body = parts.slice(1).join("\n");

        const titleMaxWidth = pageWidth - margin * 2 - 8;
        const titleLines = doc.splitTextToSize(title, titleMaxWidth);

        const bodyMaxWidth = pageWidth - margin * 2 - 12;
        const bodyLines = body ? doc.splitTextToSize(body, bodyMaxWidth) : [];

        checkPageBreak((titleLines.length + bodyLines.length) * lineHeight + 12);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
        doc.text(titleLines, margin + 4, yPos);
        yPos += titleLines.length * lineHeight;

        if (bodyLines.length > 0) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9.5);
          doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
          doc.text(bodyLines, margin + 6, yPos);
          yPos += bodyLines.length * lineHeight;
        }

        yPos += 5;
      });

      yPos += 2;
    });
  };

  // En-tête
  doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
  doc.rect(0, 0, pageWidth, 65, "F");
  doc.setFillColor(colors.secondary.r, colors.secondary.g, colors.secondary.b);
  doc.rect(0, 65, pageWidth, 3, "F");

  try {
    doc.addImage(logoImage, "PNG", pageWidth / 2 - 18, 6, 36, 36);
  } catch (error) {
    console.log("Logo non charge");
  }

  doc.setTextColor(colors.accent.r, colors.accent.g, colors.accent.b);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(BRAND.name.line1, pageWidth / 2, 48, { align: "center" });

  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.setFontSize(16);
  doc.text(BRAND.name.line2, pageWidth / 2, 55, { align: "center" });

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text(BRAND.baseline.parts.text1, pageWidth / 2 - 15, 62, { align: "right" });
  doc.setTextColor(colors.accent.r, colors.accent.g, colors.accent.b);
  doc.text(" & ", pageWidth / 2, 62, { align: "center" });
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.text(BRAND.baseline.parts.text2, pageWidth / 2 + 15, 62, { align: "left" });

  yPos = 78;

  doc.setFontSize(18);
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.setFont("helvetica", "bold");
  doc.text("COMPTE RENDU DE SEANCE", pageWidth / 2, yPos, { align: "center" });

  yPos += 12;

  const infoBoxHeight = childInfo ? 30 : 22;
  doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
  doc.roundedRect(margin, yPos, pageWidth - margin * 2, infoBoxHeight, 3, 3, "F");

  doc.setFontSize(12);
  doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
  doc.setFont("helvetica", "bold");
  
  if (childInfo) {
    doc.text(`Enfant: ${childInfo.name}${childInfo.age ? ` (${childInfo.age})` : ''}`, margin + 5, yPos + 8);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text(`Parent/Responsable: ${patientName}`, margin + 5, yPos + 15);
    doc.text(`Accompagnement: ${report.accompaniment_title}`, margin + 5, yPos + 22);
  } else {
    doc.text(`Consultant: ${patientName}`, margin + 5, yPos + 8);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text(`Accompagnement: ${report.accompaniment_title}`, margin + 5, yPos + 15);
  }

  doc.setFontSize(9);
  doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
  const dateStr = new Date(report.report_date).toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  doc.text(`Date: ${dateStr}`, pageWidth - margin - 5, yPos + 8, { align: "right" });

  yPos += infoBoxHeight + 10;

  // Generate filename with child name if applicable
  const fileName = childInfo 
    ? `compte-rendu-seance-${childInfo.name.toLowerCase().replace(/\s+/g, "-")}-${report.report_date}.pdf`
    : `compte-rendu-seance-${patientName.toLowerCase().replace(/\s+/g, "-")}-${report.report_date}.pdf`;

  // Filtrer les items avec contenu réel
  const hasContent = (items: SessionReportItem[] | undefined) => 
    items?.some((item) => item.content && item.content.trim()) || false;

  if (hasContent(report.hygiene_vie)) {
    addSection("1. AJUSTEMENTS D'HYGIENE DE VIE");
    addItems(report.hygiene_vie);
    yPos += 6;
  }

  if (hasContent(report.conseils_alimentaires)) {
    addSection("2. CONSEILS ALIMENTAIRES");
    addItems(report.conseils_alimentaires);
    yPos += 6;
  }

  const manualSoutiensNaturels = report.soutiens_naturels?.filter((item) => item.source !== "analysis" && item.content && item.content.trim()) || [];
  if (manualSoutiensNaturels.length > 0) {
    addSection("3. SOUTIENS NATURELS RECOMMANDES");
    addItems(manualSoutiensNaturels);
    yPos += 6;
  }

  if (hasContent(report.routine_bien_etre)) {
    addSection("4. ROUTINE BIEN-ETRE PERSONNALISEE");
    addItems(report.routine_bien_etre);
    yPos += 6;
  }

  const analysisRecommendations = report.soutiens_naturels?.filter((item) => item.source === "analysis" && item.content && item.content.trim()) || [];
  if (analysisRecommendations.length > 0) {
    addSection("5. RECOMMANDATIONS DE L'ANALYSE");
    addItemsGroupedByType(analysisRecommendations);
    yPos += 6;
  }

  if (report.notes && report.notes.trim()) {
    checkPageBreak(30);
    addSection("NOTES COMPLEMENTAIRES");

    const maxWidth = pageWidth - margin * 2 - 8;
    const lines = doc.splitTextToSize(report.notes, maxWidth);

    doc.setFillColor(colors.background.r, colors.background.g, colors.background.b);
    const boxHeight = lines.length * lineHeight + 6;
    doc.roundedRect(margin + 2, yPos - 3, pageWidth - margin * 2 - 4, boxHeight, 2, 2, "F");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text(lines, margin + 6, yPos + 2);
    yPos += lines.length * lineHeight + 10;
  }

  // Signature
  if (signatureDataUrl) {
    checkPageBreak(50);
    yPos += 10;

    doc.setFontSize(10);
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.setFont("helvetica", "bold");
    doc.text("Signature du praticien:", margin, yPos);
    yPos += 8;

    try {
      doc.addImage(signatureDataUrl, "PNG", margin, yPos, 50, 25);
      yPos += 30;
    } catch (error) {
      console.log("Signature non chargee");
    }
  }

  // Footer sur toutes les pages
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setDrawColor(colors.accent.r, colors.accent.g, colors.accent.b);
    doc.setLineWidth(1);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
    doc.setFontSize(8);
    doc.setTextColor(colors.textLight.r, colors.textLight.g, colors.textLight.b);
    doc.setFont("helvetica", "normal");
    doc.text(`Page ${i} sur ${totalPages}`, pageWidth / 2, pageHeight - 8, { align: "center" });
    doc.setFontSize(7);
    doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
    doc.text(BRAND.legal.confidential, margin, pageHeight - 8);
    doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
    doc.text(`${BRAND.name.full} - ${BRAND.baseline.full}`, pageWidth - margin, pageHeight - 8, { align: "right" });
  }

  // Sauvegarder le PDF avec le nom approprié
  doc.save(fileName);
};
